import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Bell, Search, Filter, Store as StoreIcon, RefreshCw, Check,
  Clock, ListTodo, X as XIcon, CheckCircle2, MessageSquareText, Camera, Circle,
  ChevronLeft, ChevronRight, Download, ClipboardCheck, Play, Flag,
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { notifications as notifApi } from "../lib/api";
import { localizedCategory } from "../lib/i18nContent";

const KIND_META = {
  finding_started:      { icon: Clock,             tone: "text-amber-600 bg-amber-50",     label_key: "notifications.kind_started" },
  finding_done:         { icon: CheckCircle2,      tone: "text-emerald-600 bg-emerald-50", label_key: "notifications.kind_done" },
  finding_cannot_do:    { icon: XIcon,             tone: "text-red-600 bg-red-50",         label_key: "notifications.kind_cannot_do" },
  finding_reopened:     { icon: ListTodo,          tone: "text-stone-700 bg-stone-100",    label_key: "notifications.kind_reopened" },
  finding_note_updated: { icon: MessageSquareText, tone: "text-blue-600 bg-blue-50",       label_key: "notifications.kind_note" },
  finding_photo_added:  { icon: Camera,            tone: "text-fuchsia-600 bg-fuchsia-50", label_key: "notifications.kind_photo" },
  assignment_assigned:    { icon: ClipboardCheck,    tone: "text-blue-700 bg-blue-50",       label_key: "notifications.kind_assignment_assigned" },
  assignment_reassigned:  { icon: RefreshCw,         tone: "text-indigo-600 bg-indigo-50",   label_key: "notifications.kind_assignment_reassigned" },
  assignment_started:     { icon: Play,              tone: "text-amber-600 bg-amber-50",     label_key: "notifications.kind_assignment_started" },
  assignment_completed:   { icon: CheckCircle2,      tone: "text-emerald-600 bg-emerald-50", label_key: "notifications.kind_assignment_completed" },
  assignment_comment:     { icon: MessageSquareText, tone: "text-sky-600 bg-sky-50",         label_key: "notifications.kind_assignment_comment" },
  audit_flag:             { icon: Flag,              tone: "text-red-600 bg-red-50",         label_key: "notifications.kind_audit_flag" },
};
const KIND_OPTIONS = Object.keys(KIND_META);
const PAGE_SIZE = 50;

export default function NotificationsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  // Filters
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [storeName, setStoreName] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const searchTimer = useRef(null);

  const params = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset,
      q: q.trim() || undefined,
      kind: kind === "all" ? undefined : kind,
      store_name: storeName.trim() || undefined,
      unread_only: unreadOnly || undefined,
    }),
    [q, kind, storeName, unreadOnly, offset],
  );

  const load = async () => {
    setLoading(true);
    try {
      const resp = await notifApi.list(params);
      const rows = Array.isArray(resp) ? resp : resp?.items || [];
      setItems(rows);
      setTotal(resp?.total ?? rows.length);
      setUnread(resp?.unread ?? 0);
    } catch {
      toast.error(t("common.error", "Error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, storeName, unreadOnly, offset]);

  // Debounced text search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setOffset(0);
      load();
    }, 300);
    return () => clearTimeout(searchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Available store names for the store selector — derived from current items
  const storeOptions = useMemo(() => {
    const set = new Set(items.map((n) => n.store_name).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const markAllRead = async () => {
    try {
      await notifApi.markAllRead();
      toast.success(t("notifications.mark_all_read", "Marcar todo leído"));
      load();
    } catch {
      toast.error(t("common.error", "Error"));
    }
  };

  const openItem = async (n) => {
    if (!n.read) {
      try { await notifApi.markRead(n.id); } catch { /* noop */ }
      setItems((rows) => rows.map((r) => (r.id === n.id ? { ...r, read: true } : r)));
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.assignment_id) navigate(`/assignments/${n.assignment_id}`);
    else if (n.finding_id) navigate(`/findings?highlight=${n.finding_id}`);
    else if (n.audit_id) navigate(`/summary/${n.audit_id}`);
  };

  const clearFilters = () => {
    setQ("");
    setKind("all");
    setStoreName("");
    setUnreadOnly(false);
    setOffset(0);
  };

  const exportCsv = async () => {
    try {
      // Reuse the same active filters (but not pagination) so the file matches
      // exactly what the user sees on screen.
      const csvParams = {
        q: q.trim() || undefined,
        kind: kind === "all" ? undefined : kind,
        store_name: storeName.trim() || undefined,
        unread_only: unreadOnly || undefined,
      };
      const blob = await notifApi.exportCsv(csvParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `canales-notifications-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("notifications.exported", "CSV descargado"));
    } catch {
      toast.error(t("common.error", "Error"));
    }
  };

  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(total, offset + items.length);
  const canNext = offset + PAGE_SIZE < total;
  const canPrev = offset > 0;

  return (
    <div className="p-6 md:p-8" data-testid="notifications-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="canales-overline flex items-center gap-2">
            <Bell size={13} /> {t("notifications.title", "Notificaciones")}
          </div>
          <h1 className="font-display text-3xl md:text-4xl mt-1 tracking-tight text-stone-900">
            {t("notifications.page_title", "Historial de actividad")}
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            {t("notifications.page_subtitle", "Cada movimiento en un link TO DO queda registrado aquí.")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5" data-testid="notif-refresh">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            {t("common.refresh", "Actualizar")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={loading || total === 0}
            className="gap-1.5"
            data-testid="notif-export-csv"
          >
            <Download size={13} /> {t("notifications.export_csv", "Exportar CSV")}
          </Button>
          <Button
            size="sm"
            onClick={markAllRead}
            disabled={unread === 0}
            className="brand-button gap-1.5"
            data-testid="notif-mark-all-read"
          >
            <Check size={13} /> {t("notifications.mark_all_read", "Marcar todo leído")}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mt-4 max-w-md">
        <Card className="canales-card p-3">
          <div className="canales-overline">{t("notifications.total", "Total")}</div>
          <div className="text-xl font-semibold tabular-nums">{total}</div>
        </Card>
        <Card className="canales-card p-3">
          <div className="canales-overline">{t("notifications.unread", "Sin leer")}</div>
          <div className="text-xl font-semibold tabular-nums text-red-600">{unread}</div>
        </Card>
        <Card className="canales-card p-3">
          <div className="canales-overline">{t("notifications.showing", "Mostrando")}</div>
          <div className="text-xl font-semibold tabular-nums">{items.length}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="canales-card mt-4 p-3 md:p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-center">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("notifications.search_ph", "Buscar por descripción, autor…")}
              className="pl-7 h-9 text-sm"
              data-testid="notif-search"
            />
          </div>
          <Select value={kind} onValueChange={(v) => { setOffset(0); setKind(v); }}>
            <SelectTrigger className="h-9 text-sm" data-testid="notif-filter-kind">
              <div className="flex items-center gap-1.5">
                <Filter size={12} />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("notifications.filter_all", "Todos los tipos")}</SelectItem>
              {KIND_OPTIONS.map((k) => (
                <SelectItem key={k} value={k}>
                  {t(KIND_META[k].label_key)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <StoreIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              value={storeName}
              onChange={(e) => { setOffset(0); setStoreName(e.target.value); }}
              placeholder={t("notifications.filter_store_ph", "Filtrar por tienda…")}
              className="pl-7 h-9 text-sm"
              list="notif-store-list"
              data-testid="notif-filter-store"
            />
            <datalist id="notif-store-list">
              {storeOptions.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div className="flex items-center gap-2 justify-between">
            <label className="flex items-center gap-1.5 text-xs font-medium text-stone-600 cursor-pointer">
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(e) => { setOffset(0); setUnreadOnly(e.target.checked); }}
                className="accent-[#0B1220]"
                data-testid="notif-filter-unread"
              />
              {t("notifications.only_unread", "Solo sin leer")}
            </label>
            {(q || kind !== "all" || storeName || unreadOnly) && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-[11px] text-stone-500 hover:text-stone-800 underline"
                data-testid="notif-clear-filters"
              >
                {t("common.clear", "Limpiar")}
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* List */}
      <Card className="canales-card mt-4" data-testid="notif-list">
        {loading && items.length === 0 && (
          <div className="p-8 text-center text-sm text-stone-500">
            {t("common.loading", "Cargando…")}
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-sm text-stone-500">
            {t("notifications.empty_filtered", "No hay notificaciones que coincidan con los filtros.")}
          </div>
        )}
        <ul className="divide-y divide-stone-100">
          {items.map((n) => {
            const meta = KIND_META[n.kind] || { icon: Circle, tone: "text-stone-500 bg-stone-100", label_key: null };
            const Icon = meta.icon;
            const kindLabel = meta.label_key ? t(meta.label_key) : n.kind;
            const when = (n.created_at || "").slice(0, 19).replace("T", " ");
            return (
              <li
                key={n.id}
                onClick={() => openItem(n)}
                className={`px-4 py-3 flex items-start gap-3 cursor-pointer transition-colors ${
                  n.read ? "hover:bg-stone-50" : "bg-blue-50/40 hover:bg-blue-50/70"
                }`}
                data-testid={`notif-item-${n.id}`}
              >
                <span className={`inline-flex w-8 h-8 rounded-full ${meta.tone} items-center justify-center shrink-0`}>
                  <Icon size={14} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap text-[11px]">
                    <span className="font-semibold text-stone-800 text-sm">{kindLabel}</span>
                    {!n.read && (
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />
                    )}
                    {n.store_name && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600">
                        <StoreIcon size={9} /> {n.store_name}
                      </span>
                    )}
                    {n.severity && (
                      <span className={`inline-flex px-1.5 py-0.5 rounded-full font-medium ring-1 ring-inset ${
                        n.severity === "Alta" ? "bg-red-50 text-red-700 ring-red-600/20"
                        : n.severity === "Media" ? "bg-amber-50 text-amber-700 ring-amber-600/20"
                        : "bg-stone-100 text-stone-700 ring-stone-400/20"
                      }`}>
                        {t(`severity.${n.severity}`, n.severity)}
                      </span>
                    )}
                    <span className="ml-auto text-[10.5px] text-stone-400 tabular-nums shrink-0">{when}</span>
                  </div>
                  <div className="text-[13px] text-stone-700 mt-1">
                    {n.category && (
                      <span className="font-medium">
                        {localizedCategory(n.category, i18n.language)}:
                      </span>
                    )}{" "}
                    {n.question || ""}
                  </div>
                  <div className="text-[11.5px] text-stone-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span>{t("notifications.by", "por")} <strong className="text-stone-700">{n.sender_name || "Anonymous"}</strong></span>
                    {n.from_status && n.to_status && (
                      <span className="font-mono text-[10.5px] px-1.5 py-0.5 rounded bg-stone-100">
                        {n.from_status} → {n.to_status}
                      </span>
                    )}
                    {n.note_preview && <span className="italic">&ldquo;{n.note_preview}&rdquo;</span>}
                    {n.photo_url && (
                      <a
                        href={n.photo_url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-[#1E3A8A] hover:underline"
                      >
                        <Camera size={10} /> {t("notifications.view_photo", "Ver foto")}
                      </a>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-stone-100 text-xs text-stone-500">
            <span>
              {t("notifications.pagination", "Mostrando {{from}}–{{to}} de {{total}}", {
                from: showingFrom, to: showingTo, total,
              })}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={!canPrev || loading}
                className="h-7 px-2"
                data-testid="notif-page-prev"
              >
                <ChevronLeft size={13} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={!canNext || loading}
                className="h-7 px-2"
                data-testid="notif-page-next"
              >
                <ChevronRight size={13} />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
